import React, { useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'

import PropTypes from 'prop-types'

/**
 * Floating Action Popover for Source Document Text Interactions (Issue #718).
 * Enables copying selected/clicked text, syncing to existing review drawer entities,
 * or adding arbitrary text to the case as Diagnosis, Provider, Medication, Allergy, Injury, or Demographics.
 */
export default function SourceTextActionPopover({
  text = '',
  sourceSnippet = '',
  boxLabel = '',
  stitchedEmail = '',
  stitchedValue = '',
  page = 1,
  documentId,
  coords = null, // { x, y, width, height }
  matchedEntity = null, // Entity object if selection matches an existing drawer card
  onCopy,
  onViewMatchedEntity,
  onAddEntity,
  onClose,
}) {
  const popoverRef = useRef(null)
  const [copied, setCopied] = useState(false)
  const [copiedEmail, setCopiedEmail] = useState(false)
  const [copiedValue, setCopiedValue] = useState(false)
  const [showDemographics, setShowDemographics] = useState(false)

  // Close on Escape or click outside
  useEffect(() => {
    const handleKeyDown = e => {
      if (e.key === 'Escape') {
        if (showDemographics) {
          setShowDemographics(false)
        } else if (onClose) {
          onClose()
        }
      }
    }

    const handleClickOutside = e => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        if (onClose) onClose()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [onClose, showDemographics])

  if (!text || !coords) return null

  const handleCopyClick = async () => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text.trim())
      }
      setCopied(true)
      if (onCopy) onCopy(text.trim())
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // Fallback
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    }
  }

  const handleCopyEmail = async () => {
    if (!stitchedEmail) return
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(stitchedEmail.trim())
      }
      setCopiedEmail(true)
      if (onCopy) onCopy(stitchedEmail.trim())
      setTimeout(() => setCopiedEmail(false), 1500)
    } catch {
      setCopiedEmail(true)
      setTimeout(() => setCopiedEmail(false), 1500)
    }
  }

  const handleCopyValue = async () => {
    if (!stitchedValue) return
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(stitchedValue.trim())
      }
      setCopiedValue(true)
      if (onCopy) onCopy(stitchedValue.trim())
      setTimeout(() => setCopiedValue(false), 1500)
    } catch {
      setCopiedValue(true)
      setTimeout(() => setCopiedValue(false), 1500)
    }
  }

  const handleAdd = (category, field) => {
    if (onAddEntity) {
      onAddEntity({
        category,
        field,
        value: text.trim(),
        snippet: sourceSnippet || text.trim(),
        page,
        documentId,
      })
    }
    if (onClose) onClose()
  }

  // Calculate clamped viewport position
  const popoverWidth = 340
  const popoverHeight = showDemographics ? 290 : 210

  const targetX = coords.x + (coords.width ? coords.width / 2 : 0)
  let left = targetX - popoverWidth / 2
  if (typeof window !== 'undefined') {
    left = Math.max(12, Math.min(left, window.innerWidth - popoverWidth - 16))
  }

  // Position above the selection if space permits, otherwise below
  let top = coords.y - popoverHeight - 10
  if (top < 10) {
    top = coords.y + (coords.height || 24) + 10
  }

  const displayText = text.length > 60 ? `${text.slice(0, 58)}…` : text

  const content = (
    <div
      ref={popoverRef}
      className="hitl-source-popover shadow-lg border rounded p-2"
      style={{
        position: 'fixed',
        left: `${left}px`,
        top: `${top}px`,
        width: `${popoverWidth}px`,
        backgroundColor: '#ffffff',
        zIndex: 1060, // Above modals and canvases
      }}
      role="dialog"
      aria-label="Document Selection Actions"
    >
      {/* Popover Header / Snippet Preview */}
      <div className="d-flex align-items-center justify-content-between pb-1 mb-2 border-bottom">
        <div className="d-flex align-items-center text-truncate mr-2">
          {boxLabel && (
            <span
              className="badge badge-primary mr-1 text-truncate"
              style={{ fontSize: '0.68rem', maxWidth: '130px' }}
              title={boxLabel}
            >
              {boxLabel}
            </span>
          )}
          <span className="small font-weight-bold text-truncate text-dark" title={text}>
            &ldquo;{displayText}&rdquo;
          </span>
        </div>
        <button
          type="button"
          className="btn btn-xs btn-link text-muted p-0 ml-1"
          onClick={onClose}
          title="Close actions"
          style={{ textDecoration: 'none' }}
        >
          <i className="fas fa-times" />
        </button>
      </div>

      {/* Primary Actions */}
      <div className="d-flex flex-wrap align-items-center mb-2" style={{ gap: '6px' }}>
        <button
          type="button"
          className={`btn btn-xs ${copied ? 'btn-success text-white' : 'btn-outline-secondary'}`}
          onClick={handleCopyClick}
          style={{ fontSize: '0.78rem', flex: matchedEntity ? '0 0 auto' : '1 1 auto' }}
          title="Copy selected text to clipboard"
        >
          <i className={`mr-1 ${copied ? 'fas fa-check' : 'far fa-copy'}`} />
          {copied ? 'Copied!' : 'Copy Text'}
        </button>

        {stitchedEmail && stitchedEmail !== text.trim() && (
          <button
            type="button"
            className={`btn btn-xs ${copiedEmail ? 'btn-success text-white' : 'btn-outline-info'}`}
            onClick={handleCopyEmail}
            style={{ fontSize: '0.78rem' }}
            title={`Copy email: ${stitchedEmail}`}
          >
            <i className={`mr-1 ${copiedEmail ? 'fas fa-check' : 'far fa-envelope'}`} />
            {copiedEmail ? 'Copied!' : 'Copy Email'}
          </button>
        )}

        {stitchedValue &&
          stitchedValue !== text.trim() &&
          stitchedValue !== stitchedEmail &&
          stitchedValue.length < 80 && (
            <button
              type="button"
              className={`btn btn-xs ${copiedValue ? 'btn-success text-white' : 'btn-outline-secondary'}`}
              onClick={handleCopyValue}
              style={{ fontSize: '0.78rem' }}
              title={`Copy full cell: ${stitchedValue}`}
            >
              <i className={`mr-1 ${copiedValue ? 'fas fa-check' : 'far fa-file-alt'}`} />
              {copiedValue ? 'Copied!' : 'Copy Cell'}
            </button>
          )}

        {matchedEntity && (
          <button
            type="button"
            className="btn btn-xs btn-warning text-dark font-weight-bold flex-grow-1"
            onClick={() => {
              if (onViewMatchedEntity) onViewMatchedEntity(matchedEntity.id)
              if (onClose) onClose()
            }}
            style={{ fontSize: '0.78rem' }}
            title="View this matched entity in the review drawer"
          >
            <i className="fas fa-search-location mr-1" />
            Sync Card
          </button>
        )}
      </div>

      {/* Add-to-Case Section */}
      <div className="hitl-popover-add-section border-top pt-2">
        <div className="small font-weight-bold text-muted mb-1" style={{ fontSize: '0.72rem', letterSpacing: '0.3px' }}>
          <i className="fas fa-plus-circle mr-1 text-primary" />
          ADD TO CASE AS:
        </div>

        <div className="d-flex flex-wrap" style={{ gap: '4px' }}>
          <button
            type="button"
            className="btn btn-xs btn-outline-primary font-weight-bold"
            onClick={() => handleAdd('diagnosis', 'Diagnosis')}
            style={{ fontSize: '0.75rem', padding: '0.2rem 0.5rem' }}
          >
            <i className="fas fa-notes-medical mr-1" /> Diagnosis
          </button>

          <button
            type="button"
            className="btn btn-xs btn-outline-info font-weight-bold"
            onClick={() => handleAdd('provider', 'Treatment Provider')}
            style={{ fontSize: '0.75rem', padding: '0.2rem 0.5rem' }}
          >
            <i className="fas fa-user-md mr-1" /> Provider
          </button>

          <button
            type="button"
            className="btn btn-xs btn-outline-success font-weight-bold"
            onClick={() => handleAdd('medication', 'Medication')}
            style={{ fontSize: '0.75rem', padding: '0.2rem 0.5rem' }}
          >
            <i className="fas fa-pills mr-1" /> Medication
          </button>

          <button
            type="button"
            className="btn btn-xs btn-outline-danger font-weight-bold"
            onClick={() => handleAdd('allergy', 'Allergy')}
            style={{ fontSize: '0.75rem', padding: '0.2rem 0.5rem' }}
          >
            <i className="fas fa-allergies mr-1" /> Allergy
          </button>

          <button
            type="button"
            className="btn btn-xs btn-outline-secondary font-weight-bold"
            onClick={() => handleAdd('demographics', 'Injury Description')}
            style={{ fontSize: '0.75rem', padding: '0.2rem 0.5rem' }}
          >
            <i className="fas fa-crutch mr-1" /> Injury / Part
          </button>

          <button
            type="button"
            className={`btn btn-xs ${
              showDemographics ? 'btn-secondary text-white' : 'btn-outline-dark'
            } font-weight-bold`}
            onClick={() => setShowDemographics(prev => !prev)}
            style={{ fontSize: '0.75rem', padding: '0.2rem 0.5rem' }}
          >
            <i className="fas fa-id-card mr-1" /> Demographics {showDemographics ? '▲' : '▼'}
          </button>
        </div>

        {/* Submenu for Demographics */}
        {showDemographics && (
          <div className="bg-light border rounded p-1 mt-2 d-flex flex-column" style={{ gap: '2px' }}>
            <button
              type="button"
              className="btn btn-xs btn-link text-dark text-left py-1 px-2"
              onClick={() => handleAdd('demographics', 'Claimant Name')}
              style={{ fontSize: '0.75rem', textDecoration: 'none' }}
            >
              <i className="fas fa-user mr-2 text-muted" /> Claimant Name
            </button>
            <button
              type="button"
              className="btn btn-xs btn-link text-dark text-left py-1 px-2"
              onClick={() => handleAdd('demographics', 'Date of Injury')}
              style={{ fontSize: '0.75rem', textDecoration: 'none' }}
            >
              <i className="fas fa-calendar-day mr-2 text-muted" /> Date of Injury
            </button>
            <button
              type="button"
              className="btn btn-xs btn-link text-dark text-left py-1 px-2"
              onClick={() => handleAdd('demographics', 'Date of Birth')}
              style={{ fontSize: '0.75rem', textDecoration: 'none' }}
            >
              <i className="fas fa-birthday-cake mr-2 text-muted" /> Date of Birth
            </button>
            <button
              type="button"
              className="btn btn-xs btn-link text-dark text-left py-1 px-2"
              onClick={() => handleAdd('referral', 'Claim Number')}
              style={{ fontSize: '0.75rem', textDecoration: 'none' }}
            >
              <i className="fas fa-hashtag mr-2 text-muted" /> Claim Number
            </button>
          </div>
        )}
      </div>
    </div>
  )

  if (typeof document !== 'undefined' && document.body) {
    return ReactDOM.createPortal(content, document.body)
  }
  return content
}

SourceTextActionPopover.propTypes = {
  text: PropTypes.string,
  sourceSnippet: PropTypes.string,
  boxLabel: PropTypes.string,
  stitchedEmail: PropTypes.string,
  stitchedValue: PropTypes.string,
  page: PropTypes.number,
  documentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  coords: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number,
    width: PropTypes.number,
    height: PropTypes.number,
  }),
  matchedEntity: PropTypes.object,
  onCopy: PropTypes.func,
  onViewMatchedEntity: PropTypes.func,
  onAddEntity: PropTypes.func,
  onClose: PropTypes.func,
}
